// ==================== PROFILE SETTINGS PAGE ====================

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Mail, 
  Building,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Camera,
  Lock,
  Eye,
  EyeOff,
  Shield,
} from 'lucide-react';
import { Button, Input } from '../../../components/ui';
import { useAuth } from '../../../contexts/auth';
import { authService } from '../../../services';
import { MESSAGES } from '../../../constants/messages.constants';

// ==================== VALIDATION SCHEMAS ====================

const profileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[@$!%*?&]/, 'Password must contain at least one special character (@$!%*?&)'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: MESSAGES.ERROR.PASSWORD_MISMATCH,
  path: ['confirmPassword'],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

// ==================== PASSWORD INPUT COMPONENT ====================

interface PasswordInputProps {
  label: string;
  placeholder?: string;
  error?: string;
  register: ReturnType<typeof useForm<PasswordFormData>>['register'];
  name: keyof PasswordFormData;
}

const PasswordInput = ({ label, placeholder, error, register, name }: PasswordInputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <div>
      <label className="block text-xs font-medium text-gray-300 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type={showPassword ? 'text' : 'password'}
          placeholder={placeholder}
          {...register(name)}
          className={`w-full pl-10 pr-10 py-2.5 text-sm bg-[#0D1117] border rounded-lg text-white placeholder-gray-500 
                     focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all
                     ${error ? 'border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Profile Form
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    },
  });

  // Password Form
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors, isValid: isPasswordValid },
    reset: resetPasswordForm,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    mode: 'onChange',
  });

  const showSuccessMessage = (message: string) => {
    setSuccess(message);
    setError(null);
    setTimeout(() => setSuccess(null), 3000);
  };

  const showErrorMessage = (message: string) => {
    setError(message);
    setSuccess(null);
  };

  // Handle Profile Submit
  const onSubmit = async (data: ProfileFormData) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await authService.updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
      });
      
      if (response.success && response.data) {
        updateUser(response.data);
        showSuccessMessage('Profile updated successfully!');
      } else {
        showErrorMessage(response.message || 'Failed to update profile');
      }
    } catch {
      showErrorMessage('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Handle Password Change
  const onPasswordSubmit = async (data: PasswordFormData) => {
    try {
      setChangingPassword(true);
      setError(null);
      setSuccess(null);

      const response = await authService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      
      if (response.success) {
        resetPasswordForm();
        setShowPasswordForm(false);
        showSuccessMessage('Password changed successfully!');
      } else {
        showErrorMessage(response.message || 'Failed to change password');
      }
    } catch {
      showErrorMessage('Failed to change password. Please check your current password.');
    } finally {
      setChangingPassword(false);
    }
  };

  const getInitials = () => {
    const first = user?.firstName?.charAt(0) || '';
    const last = user?.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-base font-heading font-semibold text-white">Profile Settings</h2>
        <p className="text-xs text-(--color-muted-gray) mt-0.5">
          Manage your personal information and preferences
        </p>
      </div>

      {/* Success/Error Messages */}
      <AnimatePresence>
        {success && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <p className="text-sm text-emerald-400">{success}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 text-xs"
            >
              Dismiss
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#121723] rounded-xl border border-gray-800 overflow-hidden"
      >
        {/* Avatar Section */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-xl bg-linear-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <span className="text-xl font-bold text-white">{getInitials()}</span>
              </div>
              <button className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#1A2332] border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white hover:border-orange-500 transition-colors">
                <Camera className="w-3 h-3" />
              </button>
            </div>
            <div>
              <h3 className="text-sm font-heading font-semibold text-white">
                {user?.firstName} {user?.lastName}
              </h3>
              <p className="text-xs text-(--color-muted-gray)">{user?.email}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {user?.organization?.name || 'No organization'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1.5">
                First Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  {...register('firstName')}
                  placeholder="Enter first name"
                  error={errors.firstName?.message}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1.5">
                Last Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  {...register('lastName')}
                  placeholder="Enter last name"
                  error={errors.lastName?.message}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1.5">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={user?.email || ''}
                disabled
                className="pl-10 opacity-60 cursor-not-allowed"
              />
            </div>
            <p className="text-[11px] text-(--color-muted-gray) mt-1">
              Email cannot be changed. Contact support for assistance.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1.5">
              Organization
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={user?.organization?.name || 'No organization'}
                disabled
                className="pl-10 opacity-60 cursor-not-allowed"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-800">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!isDirty || saving}
            >
              {saving ? (
                <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-3.5 h-3.5 mr-1.5" /> Save Changes</>
              )}
            </Button>
          </div>
        </form>
      </motion.div>

      {/* Account Security */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#121723] rounded-xl border border-gray-800 p-4"
      >
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-orange-400" />
          <h3 className="text-sm font-heading font-semibold text-white">Account Security</h3>
        </div>
        
        <div className="space-y-3">
          {/* Password Section */}
          <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white font-medium">Password</p>
                <p className="text-xs text-(--color-muted-gray)">
                  Secure your account with a strong password
                </p>
              </div>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => {
                  setShowPasswordForm(!showPasswordForm);
                  if (!showPasswordForm) {
                    resetPasswordForm();
                    setError(null);
                  }
                }}
              >
                {showPasswordForm ? 'Cancel' : 'Change Password'}
              </Button>
            </div>

            {/* Password Change Form */}
            <AnimatePresence>
              {showPasswordForm && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleSubmitPassword(onPasswordSubmit)}
                  className="mt-4 pt-4 border-t border-gray-700 space-y-3"
                >
                  <PasswordInput
                    label="Current Password"
                    placeholder="Enter current password"
                    register={registerPassword}
                    name="currentPassword"
                    error={passwordErrors.currentPassword?.message}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <PasswordInput
                      label="New Password"
                      placeholder="Enter new password"
                      register={registerPassword}
                      name="newPassword"
                      error={passwordErrors.newPassword?.message}
                    />
                    <PasswordInput
                      label="Confirm New Password"
                      placeholder="Confirm new password"
                      register={registerPassword}
                      name="confirmPassword"
                      error={passwordErrors.confirmPassword?.message}
                    />
                  </div>

                  {/* Password Requirements */}
                  <div className="p-3 rounded-lg bg-[#1A2332] border border-gray-700">
                    <p className="text-xs font-medium text-gray-300 mb-1.5">Password Requirements:</p>
                    <ul className="text-[11px] text-(--color-muted-gray) space-y-0.5">
                      <li>• At least 8 characters long</li>
                      <li>• Contains at least one uppercase letter</li>
                      <li>• Contains at least one lowercase letter</li>
                      <li>• Contains at least one number</li>
                      <li>• Contains at least one special character (@$!%*?&)</li>
                    </ul>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={!isPasswordValid || changingPassword}
                    >
                      {changingPassword ? (
                        <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Updating...</>
                      ) : (
                        <><Lock className="w-3.5 h-3.5 mr-1.5" /> Update Password</>
                      )}
                    </Button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* Email Verification Status */}
          <div className="flex items-center justify-between p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <div>
              <p className="text-sm text-white font-medium">Email Verification</p>
              <p className="text-xs text-(--color-muted-gray)">
                {user?.isEmailVerified ? 'Your email is verified' : 'Please verify your email'}
              </p>
            </div>
            {user?.isEmailVerified ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/15 text-emerald-400 text-xs font-medium rounded-full">
                <CheckCircle className="w-3 h-3" />
                Verified
              </span>
            ) : (
              <Button variant="secondary" size="sm">
                Verify Email
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfilePage;
